// Global date formatting for FlightDeals.in.
//
// PRINCIPLE: dates are STORED and passed around internally in unambiguous ISO form
// (YYYY-MM-DD for calendar dates, full ISO-8601 for timestamps). The UI layer ONLY
// localizes for DISPLAY, and the whole product displays dates as DD/MM/YYYY (en-GB).
// Native <input type="date"> keeps its ISO value (YYYY-MM-DD) — never reformat those.

const LOCALE = 'en-GB'; // guarantees day-first (DD/MM/YYYY) display

// Accepts a Date, an ISO date string (YYYY-MM-DD) or full ISO-8601 timestamp.
function toDate(input) {
  if (!input) return null;
  if (input instanceof Date) return isNaN(input.getTime()) ? null : input;
  const s = String(input).trim();
  // Pure calendar date YYYY-MM-DD → treat as local midnight (avoid TZ off-by-one).
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (m) {
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

// DD/MM/YYYY  (e.g. '2026-03-09' -> '09/03/2026')
export function fmtDateISO(input, fallback = '') {
  const d = toDate(input);
  if (!d) return fallback;
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

// DD Mon YYYY (e.g. '09 Mar 2026') — compact, still day-first.
export function fmtDateMed(input, fallback = '') {
  const d = toDate(input);
  if (!d) return fallback;
  return d.toLocaleDateString(LOCALE, { day: '2-digit', month: 'short', year: 'numeric' });
}

// DD Mon (e.g. '09 Mar') — for tight rows like flight cards.
export function fmtDateShort(input, fallback = '') {
  const d = toDate(input);
  if (!d) return fallback;
  return d.toLocaleDateString(LOCALE, { day: '2-digit', month: 'short' });
}

// DD/MM/YYYY, HH:mm — timestamps in tables/audits.
export function fmtDateTime(input, fallback = '') {
  const d = toDate(input);
  if (!d) return fallback;
  return d.toLocaleString(LOCALE, {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

// HH:mm (24h) — flight departure/arrival times.
export function fmtTime(input, fallback = '') {
  const d = toDate(input);
  if (!d) return fallback;
  return d.toLocaleTimeString(LOCALE, { hour: '2-digit', minute: '2-digit' });
}

// Convert a DD/MM/YYYY display string back to ISO YYYY-MM-DD (rarely needed).
export function displayToISO(ddmmyyyy) {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(String(ddmmyyyy || '').trim());
  if (!m) return '';
  return `${m[3]}-${m[2]}-${m[1]}`;
}

export default { fmtDateISO, fmtDateMed, fmtDateShort, fmtDateTime, fmtTime, displayToISO };
